import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Typography,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  Divider,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  MenuItem,
  Checkbox,
  Tabs,
  Tab,
  Card,
  CardContent,
  Stack,
  Grid,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import BlockIcon from '@mui/icons-material/Block';
import { API_BASE_URL, getAuthHeaders } from '../auth';
import Materials from '../Materials';
import Receivers from '../Receivers';
import Shippers from '../Shippers';
import Projects from '../Projects';
import { PageHeader } from '../components/page-elements';

const Customer = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [users, setUsers] = useState([]);
  const [loadingCustomer, setLoadingCustomer] = useState(true);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [error, setError] = useState('');
  const [creatingUser, setCreatingUser] = useState(false);
  const [createUserError, setCreateUserError] = useState('');
  const [createUserSuccess, setCreateUserSuccess] = useState('');
  const [createdTempPassword, setCreatedTempPassword] = useState('');
  const [editingUser, setEditingUser] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    role: 'customer',
    receiveBols: false,
  });
  const [editingBusy, setEditingBusy] = useState(false);
  const [newUserForm, setNewUserForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    role: 'customer',
    receiveBols: false,
  });
  const [tabValue, setTabValue] = useState(0);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/customers/${id}/users`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        throw new Error('Failed to fetch associated users');
      }
      const data = await response.json();
      setUsers(data);
      setLoadingUsers(false);
    } catch (err) {
      setError(err.message);
      setLoadingUsers(false);
    }
  }, [id]);

  useEffect(() => {
    const fetchCustomer = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/customers/${id}`, {
          headers: getAuthHeaders(),
        });
        if (!response.ok) {
          throw new Error('Failed to fetch customer details');
        }
        const data = await response.json();
        setCustomer(data);
        setLoadingCustomer(false);
      } catch (err) {
        setError(err.message);
        setLoadingCustomer(false);
      }
    };

    fetchCustomer();
    fetchUsers();
  }, [id, fetchUsers]);

  const handleNewUserChange = (e) => {
    const { name, value } = e.target;
    setNewUserForm((prev) => ({ ...prev, [name]: value }));
  };

  const parseResponse = async (response) => {
    const text = await response.text();
    if (!text) return {};
    try {
      return JSON.parse(text);
    } catch {
      return { message: text };
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setCreatingUser(true);
    setCreateUserError('');
    setCreateUserSuccess('');
    setCreatedTempPassword('');

    if (!newUserForm.firstName || !newUserForm.lastName || !newUserForm.email) {
      setCreateUserError('First name, last name, and email are required.');
      setCreatingUser(false);
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/register`, {
        method: 'POST',
        headers: getAuthHeaders(true),
        body: JSON.stringify({
          ...newUserForm,
          customerName: id,
        }),
      });

      const data = await parseResponse(response);
      if (!response.ok) {
        throw new Error(data.message || 'Failed to create user');
      }

      setCreateUserSuccess(data.message || 'User created successfully.');
      if (data.temporaryPassword) {
        setCreatedTempPassword(data.temporaryPassword);
      }
      setNewUserForm({
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        role: 'customer',
        receiveBols: false,
      });
      await fetchUsers();
    } catch (err) {
      setCreateUserError(err.message);
    }

    setCreatingUser(false);
  };

  const openEditUser = (user) => {
    setEditingUser(user);
    setEditForm({
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      email: user.email || '',
      role: user.role || 'customer',
      receiveBols: Boolean(user.receiveBols),
    });
    setEditOpen(true);
  };

  const closeEditUser = () => {
    setEditOpen(false);
    setEditingUser(null);
    setEditForm({
      firstName: '',
      lastName: '',
      email: '',
      role: 'customer',
      receiveBols: false,
    });
  };

  const handleEditUserChange = (e) => {
    const { name, value } = e.target;
    setEditForm((prev) => ({ ...prev, [name]: value }));
  };

  const saveUserEdit = async () => {
    if (!editingUser?._id) return;
    setEditingBusy(true);
    setCreateUserError('');
    setCreateUserSuccess('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/users/${editingUser._id}`, {
        method: 'PUT',
        headers: getAuthHeaders(true),
        body: JSON.stringify({
          firstName: editForm.firstName,
          lastName: editForm.lastName,
          email: editForm.email,
          role: editForm.role,
          receiveBols: Boolean(editForm.receiveBols),
        }),
      });

      const data = await parseResponse(response);
      if (!response.ok) {
        throw new Error(data?.message || 'Failed to update user');
      }

      closeEditUser();
      await fetchUsers();
      setCreateUserSuccess('User updated successfully.');
    } catch (err) {
      setCreateUserError(err.message);
    }

    setEditingBusy(false);
  };

  const deactivateUser = async (user) => {
    if (!user?._id) return;
    setCreateUserError('');
    setCreateUserSuccess('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/users/${user._id}`, {
        method: 'PUT',
        headers: getAuthHeaders(true),
        body: JSON.stringify({ isActive: false }),
      });

      const data = await parseResponse(response);
      if (!response.ok) {
        throw new Error(data?.message || 'Failed to deactivate user');
      }

      await fetchUsers();
      setCreateUserSuccess('User deactivated successfully.');
    } catch (err) {
      setCreateUserError(err.message);
    }
  };

  if (loadingCustomer) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Typography color="error">Error: {error}</Typography>;
  }

  return (
    <Box p={2}>
      <PageHeader
        title={customer?.customerName ? `Customer: ${customer.customerName}` : 'Customer Details'}
        subtitle="Manage customer profile and related setup records from one place."
        actions={
          tabValue === 0 ? (
            <Button variant="contained" onClick={() => navigate(`/customers/update/${id}`)}>
              Edit Profile
            </Button>
          ) : null
        }
      />

      <Paper elevation={0} sx={{ border: '1px solid var(--ui-border)', borderRadius: 2, mb: 2 }}>
        <Tabs value={tabValue} onChange={(_, next) => setTabValue(next)} variant="scrollable">
          <Tab label="Overview" />
          <Tab label="Materials" />
          <Tab label="Receivers" />
          <Tab label="Shippers" />
          <Tab label="Locations" />
          <Tab label="Users" />
        </Tabs>
      </Paper>

      {tabValue === 0 && (
        <Card variant="outlined" sx={{ borderRadius: 2 }}>
          <CardContent>
            {customer ? (
              <Stack spacing={1}>
                <Typography variant="h6">Company Name: {customer.customerName}</Typography>
                <Typography>Customer Code: {customer.customerCode || 'N/A'}</Typography>
                <Typography>
                  Address: {customer.customerAddress1}
                  {customer.customerAddress2 && `, ${customer.customerAddress2}`}, {customer.customerCity},{' '}
                  {customer.customerState} {customer.customerZip}
                </Typography>
                <Typography>
                  Average Load Goal: {Number.isFinite(Number(customer.loadGoalMinutes)) ? `${Number(customer.loadGoalMinutes)} min` : '90 min'}
                </Typography>
                <Typography>
                  Ground Inventory: {customer.enableGroundInventory ? 'Enabled' : 'Disabled'}
                </Typography>
                {customer.customerLogo && (
                  <Box mt={1}>
                    <img
                      src={customer.customerLogo}
                      alt={customer.customerName}
                      style={{ maxWidth: '150px', maxHeight: '100px' }}
                    />
                  </Box>
                )}
              </Stack>
            ) : (
              <Typography>No customer data available.</Typography>
            )}
          </CardContent>
        </Card>
      )}

      {tabValue === 1 && <Materials forcedCustomerId={id} embedded />}
      {tabValue === 2 && <Receivers forcedCustomerId={id} embedded />}
      {tabValue === 3 && <Shippers forcedCustomerId={id} embedded />}
      {tabValue === 4 && <Projects forcedCustomerId={id} embedded />}

      {tabValue === 5 && (
        <>
          <Card variant="outlined" className="surface-hover" sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Associated Users
              </Typography>
            {loadingUsers ? (
              <Box display="flex" justifyContent="center" mt={2}>
                <CircularProgress />
              </Box>
            ) : users.length === 0 ? (
              <Typography>No users found for this customer.</Typography>
            ) : (
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>User ID</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Role</TableCell>
                      <TableCell>Receive BOLs</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {users.map((user) => (
                      <TableRow key={user._id}>
                        <TableCell>{user._id}</TableCell>
                        <TableCell>{user.fullName || `${user.firstName} ${user.lastName}`}</TableCell>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>{user.role || 'N/A'}</TableCell>
                        <TableCell>{user.receiveBols ? 'Yes' : 'No'}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={user.isActive === false ? 'Inactive' : 'Active'}
                            color={user.isActive === false ? 'default' : 'success'}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Edit user">
                            <span>
                              <IconButton color="primary" onClick={() => openEditUser(user)}>
                                <EditIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Deactivate user">
                            <span>
                              <IconButton
                                color="warning"
                                onClick={() => deactivateUser(user)}
                                disabled={user.isActive === false}
                              >
                                <BlockIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            </CardContent>
          </Card>

          <Box mt={2}>
            <Card variant="outlined" className="surface-hover" sx={{ borderRadius: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Register New User For This Customer
                </Typography>
                <Divider sx={{ mb: 1.5 }} />
                {createUserError ? <Alert severity="error" sx={{ mb: 1 }}>{createUserError}</Alert> : null}
                {createUserSuccess ? <Alert severity="success" sx={{ mb: 1 }}>{createUserSuccess}</Alert> : null}
                {createdTempPassword ? (
                  <Alert severity="warning" sx={{ mb: 1 }}>
                    Email was not sent. Temporary password: <strong>{createdTempPassword}</strong>
                  </Alert>
                ) : null}

                <Box component="form" onSubmit={handleCreateUser}>
                  <Grid container spacing={1.2} maxWidth={720}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="First Name"
                        name="firstName"
                        value={newUserForm.firstName}
                        onChange={handleNewUserChange}
                        fullWidth
                        required
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Last Name"
                        name="lastName"
                        value={newUserForm.lastName}
                        onChange={handleNewUserChange}
                        fullWidth
                        required
                      />
                    </Grid>
                    <Grid item xs={12} md={7}>
                      <TextField
                        label="Email"
                        name="email"
                        type="email"
                        value={newUserForm.email}
                        onChange={handleNewUserChange}
                        fullWidth
                        required
                      />
                    </Grid>
                    <Grid item xs={12} md={5}>
                      <TextField
                        label="Temporary Password"
                        name="password"
                        type="password"
                        value={newUserForm.password}
                        onChange={handleNewUserChange}
                        fullWidth
                        helperText="Optional. Leave blank to auto-generate and email."
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={Boolean(newUserForm.receiveBols)}
                            onChange={(e) =>
                              setNewUserForm((prev) => ({ ...prev, receiveBols: Boolean(e.target.checked) }))
                            }
                          />
                        }
                        label="Receive completed BOL emails"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Button type="submit" variant="contained" disabled={creatingUser}>
                        {creatingUser ? 'Registering...' : 'Register User'}
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              </CardContent>
            </Card>
          </Box>
        </>
      )}

      <Dialog open={editOpen} onClose={closeEditUser} fullWidth maxWidth="sm">
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent>
          <TextField
            label="First Name"
            name="firstName"
            value={editForm.firstName}
            onChange={handleEditUserChange}
            fullWidth
            margin="normal"
            required
          />
          <TextField
            label="Last Name"
            name="lastName"
            value={editForm.lastName}
            onChange={handleEditUserChange}
            fullWidth
            margin="normal"
            required
          />
          <TextField
            label="Email"
            name="email"
            type="email"
            value={editForm.email}
            onChange={handleEditUserChange}
            fullWidth
            margin="normal"
            required
          />
          <TextField
            label="Role"
            name="role"
            value={editForm.role}
            onChange={handleEditUserChange}
            fullWidth
            margin="normal"
            select
            required
          >
            <MenuItem value="customer">Customer</MenuItem>
            <MenuItem value="internal">Internal</MenuItem>
            <MenuItem value="admin">Admin</MenuItem>
          </TextField>
          <FormControlLabel
            control={
              <Checkbox
                checked={Boolean(editForm.receiveBols)}
                onChange={(e) =>
                  setEditForm((prev) => ({ ...prev, receiveBols: Boolean(e.target.checked) }))
                }
              />
            }
            label="Receive completed BOL emails"
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEditUser}>Cancel</Button>
          <Button variant="contained" onClick={saveUserEdit} disabled={editingBusy}>
            {editingBusy ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Customer;

